import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Form, Spinner, Alert, Table } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTimes, FaLink } from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../utils/api';
import { resolveFileUrl } from '../utils/fileUrl';
import { getActiveCompany } from '../utils/companyContext';
import { useNavigate } from 'react-router-dom';

const defaultNumberEntry = { label: '', value: '' };



const createInitialFormState = (company = null) => ({
  id: company?.id ?? null,
  name: company?.name ?? '',
  slug: company?.slug ?? '',
  logoUrl: company?.logoUrl ?? '',
  address: company?.address ?? '',
  phone: company?.phone ?? '',
  email: company?.email ?? '',
  status: company?.status ?? 'active',
  themeColor: company?.themeColor ?? 'red',
  companyNumbers:
    company?.companyNumbers?.length
      ? company.companyNumbers.map((entry) => ({
          label: entry.label ?? '',
          value: entry.value ?? '',
        }))
      : [
          { ...defaultNumberEntry },
        ],
});

const sanitizeNumbers = (numbers) => {
  if (!Array.isArray(numbers)) {
    return [];
  }
  return numbers
    .map((entry) => ({
      label: entry?.label?.trim() || '',
      value: entry?.value?.trim() || '',
    }))
    .filter((entry) => entry.label || entry.value);
};

const CompanyDetails = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const activeCompany = useMemo(() => getActiveCompany(), []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formState, setFormState] = useState(createInitialFormState());
  const [formError, setFormError] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoInputKey, setLogoInputKey] = useState(0);

  const resolvedLogoPreview = useMemo(
    () => resolveFileUrl(logoPreview || formState.logoUrl),
    [logoPreview, formState.logoUrl]
  );

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/admin/companies', {}, navigate);
      const list = Array.isArray(response.data) ? response.data : [];
      const filteredCompanies = activeCompany?.id
        ? list.filter((company) => company.id === activeCompany.id)
        : list;
      setCompanies(filteredCompanies);
    } catch (requestError) {
      console.error('Failed to load companies', requestError);
      setError(requestError.message || 'Unable to load companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [activeCompany?.id, navigate]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => () => {
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
  }, [logoPreview]);

  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setFormState(createInitialFormState());
    setFormError(null);
    setLogoFile(null);
    setLogoPreview('');
    setLogoInputKey((prev) => prev + 1);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (company) => {
    setIsEditing(true);
    setFormState(createInitialFormState(company));
    setFormError(null);
    setLogoFile(null);
    setLogoPreview(company?.logoUrl ?? '');
    setLogoInputKey((prev) => prev + 1);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (formSubmitting) return;
    setIsModalOpen(false);
    setFormError(null);
    setFormState(createInitialFormState());
    setLogoFile(null);
    setLogoPreview('');
    setLogoInputKey((prev) => prev + 1);
  };

  const updateFormField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (index, field, value) => {
    setFormState((prev) => {
      const nextNumbers = [...(prev.companyNumbers || [])];
      nextNumbers[index] = {
        ...nextNumbers[index],
        [field]: value,
      };
      return { ...prev, companyNumbers: nextNumbers };
    });
  };

  const handleAddNumber = () => {
    setFormState((prev) => ({
      ...prev,
      companyNumbers: [...(prev.companyNumbers || []), { ...defaultNumberEntry }],
    }));
  };

  const handleRemoveNumber = (index) => {
    setFormState((prev) => {
      const nextNumbers = [...(prev.companyNumbers || [])];
      nextNumbers.splice(index, 1);
      return {
        ...prev,
        companyNumbers: nextNumbers.length
          ? nextNumbers
          : [{ ...defaultNumberEntry }],
      };
    });
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    setLogoFile(file);

    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      return;
    }

    setLogoPreview(isEditing ? formState.logoUrl ?? '' : '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formSubmitting) return;

    setFormSubmitting(true);
    setFormError(null);

    const trimmedName = formState.name.trim();
    const trimmedAddress = formState.address.trim();
    const trimmedPhone = formState.phone.trim();
    const trimmedEmail = formState.email.trim();
    const sanitizedNumbers = sanitizeNumbers(formState.companyNumbers);

    if (!isEditing && !logoFile) {
      setFormError('Please select a logo file to upload.');
      setFormSubmitting(false);
      return;
    }

    const requestPath = isEditing
      ? `/api/admin/companies/${formState.id}`
      : '/api/admin/companies';
    const requestMethod = isEditing ? 'PUT' : 'POST';

    const formData = new FormData();
    formData.append('name', trimmedName);
    formData.append('address', trimmedAddress);
    formData.append('phone', trimmedPhone);
    formData.append('email', trimmedEmail);
    formData.append('status', formState.status);
    formData.append('themeColor', formState.themeColor);
    formData.append('companyNumbers', JSON.stringify(sanitizedNumbers));

    if (logoFile) {
      formData.append('logo', logoFile);
    }

    try {
      await apiRequest(
        requestPath,
        {
          method: requestMethod,
          body: formData,
        },
        navigate
      );
      setIsModalOpen(false);
      setFormState(createInitialFormState());
      setLogoFile(null);
      setLogoPreview('');
      setLogoInputKey((prev) => prev + 1);
      await loadCompanies();
    } catch (requestError) {
      console.error('Failed to save company', requestError);
      setFormError(requestError.message || 'Unable to save company');
    } finally {
      setFormSubmitting(false);
    }
  };

  const totalCompanies = useMemo(() => companies.length, [companies]);

  return (
    <DashboardLayout title="Company Details">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div>
            <h2 className="h4 mb-0">Company Directory</h2>
            <p className="text-muted mb-0">
              Manage company profiles, contact information, and supporting identifiers.
            </p>
          </div>
          <Button variant="success" onClick={handleOpenCreateModal}>
            <FaPlus className="me-2" /> Add Company
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body">
            {loading ? (
              <div className="d-flex align-items-center justify-content-center py-5">
                <Spinner animation="border" role="status" aria-hidden="true" />
                <span className="ms-3 text-muted">Loading companies...</span>
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted mb-2">No companies have been added yet.</p>
                <Button variant="success" onClick={handleOpenCreateModal}>
                  <FaPlus className="me-2" /> Create your first company
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover responsive className="align-middle">
                  <thead>
                    <tr>
                      <th scope="col">Logo</th>
                      <th scope="col">Name</th>
                      <th scope="col">Contact</th>
                      <th scope="col">Status</th>
                      <th scope="col">Theme</th>
                      <th scope="col">Numbers</th>
                      <th scope="col" className="text-end">
                        Total: {totalCompanies}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => {
                      const resolvedLogoUrl = resolveFileUrl(company.logoUrl);
                      const themeLabel = company.themeColor === 'brown' ? 'Brown' : 'Red';
                      return (
                        <tr key={company.id}>
                          <td>
                            {resolvedLogoUrl ? (
                              <img
                                src={resolvedLogoUrl}
                                alt={`${company.name} logo`}
                                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
                              />
                            ) : (
                              <div
                                className="bg-light rounded d-flex align-items-center justify-content-center"
                                style={{ width: '64px', height: '64px' }}
                                aria-label="No logo available"
                              >
                                <FaLink className="text-muted" />
                              </div>
                            )}
                          </td>
                          <td>
                            <h6 className="mb-1">{company.name}</h6>
                            <div className="text-muted small">Slug: {company.slug}</div>
                            <div className="text-muted small">{company.address}</div>
                          </td>
                          <td>
                            <div className="small text-muted">
                              {company.phone && <div>Phone: {company.phone}</div>}
                              {company.email && <div>Email: {company.email}</div>}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge rounded-pill ${
                                company.status === 'active'
                                  ? 'bg-success-subtle text-success'
                                  : 'bg-secondary-subtle text-secondary'
                              }`}
                            >
                              {company.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge rounded-pill ${
                                company.themeColor === 'brown'
                                  ? 'bg-warning-subtle text-dark'
                                  : 'bg-danger-subtle text-danger'
                              }`}
                            >
                              {themeLabel}
                            </span>
                          </td>
                          <td>
                            {company.companyNumbers?.length ? (
                              <ul className="list-unstyled mb-0 small text-muted">
                                {company.companyNumbers.map((entry, index) => (
                                  <li key={`${company.id}-number-${index}`}>
                                    {entry.label && <strong>{entry.label}: </strong>}
                                    {entry.value || '—'}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenEditModal(company)}
                            >
                              <FaEdit className="me-1" /> Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={isModalOpen} onHide={handleCloseModal} backdrop="static" centered size="lg">
        <Form onSubmit={handleSubmit} encType="multipart/form-data">
          <Modal.Header closeButton={!formSubmitting}>
            <Modal.Title>{isEditing ? 'Edit Company' : 'Add Company'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && (
              <Alert variant="danger" className="mb-3">
                {formError}
              </Alert>
            )}
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group controlId="company-name">
                  <Form.Label>
                    Company Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.name}
                    onChange={(event) => updateFormField('name', event.target.value)}
                    placeholder="Enter company name"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="company-logo">
                  <Form.Label>
                    Company Logo <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    key={logoInputKey}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoChange}
                    required={!isEditing}
                    disabled={formSubmitting}
                  />
                  <Form.Text className="text-muted">
                    Upload a square PNG, JPG, SVG, or WEBP image. Maximum size 2 MB.
                  </Form.Text>
                  {resolvedLogoPreview ? (
                    <div className="mt-3">
                      <img
                        src={resolvedLogoPreview}
                        alt="Company logo preview"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          maxHeight: '120px',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted small mt-3 mb-0">No logo selected.</p>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-12">
                <Form.Group controlId="company-address">
                  <Form.Label>
                    Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formState.address}
                    onChange={(event) => updateFormField('address', event.target.value)}
                    placeholder="Enter registered address"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="company-phone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.phone}
                    onChange={(event) => updateFormField('phone', event.target.value)}
                    placeholder="Optional contact number"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="company-email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formState.email}
                    onChange={(event) => updateFormField('email', event.target.value)}
                    placeholder="Optional contact email"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="company-status">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formState.status}
                    onChange={(event) => updateFormField('status', event.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="company-theme">
                  <Form.Label>Theme</Form.Label>
                  <Form.Select
                    value={formState.themeColor}
                    onChange={(event) => updateFormField('themeColor', event.target.value)}
                  >
                    <option value="red">Red</option>
                    <option value="brown">Brown</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group controlId="company-slug">
                  <Form.Label>Slug</Form.Label>
                  {isEditing ? (
                    <Form.Control value={formState.slug} disabled />
                  ) : (
                    <Form.Control
                      value="Generated automatically after saving"
                      disabled
                    />
                  )}
                </Form.Group>
              </div>
            </div>

            <hr className="my-4" />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Company Numbers</h5>
              <Button variant="outline-success" size="sm" onClick={handleAddNumber}>
                <FaPlus className="me-2" /> Add another
              </Button>
            </div>
            <p className="text-muted small">
              Store optional identifiers like GST, PAN, CIN, or other registration numbers.
            </p>
            <div className="row g-3">
              {formState.companyNumbers.map((entry, index) => (
                <React.Fragment key={`company-number-${index}`}>
                  <div className="col-md-5">
                    <Form.Group>
                      <Form.Label className="visually-hidden">Number label</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Label (e.g., GST)"
                        value={entry.label}
                        onChange={(event) => handleNumberChange(index, 'label', event.target.value)}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-5">
                    <Form.Group>
                      <Form.Label className="visually-hidden">Number value</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Identifier value"
                        value={entry.value}
                        onChange={(event) => handleNumberChange(index, 'value', event.target.value)}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-2 d-flex align-items-center">
                    <Button
                      variant="outline-danger"
                      className="w-100"
                      onClick={() => handleRemoveNumber(index)}
                      disabled={formState.companyNumbers.length === 1}
                    >
                      <FaTimes className="me-1" /> Remove
                    </Button>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={formSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="success" disabled={formSubmitting}>
              {formSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                'Save Company'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </DashboardLayout>
  );
};

export default CompanyDetails;
